const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/

export type YoutubeUrlErrorCode =
  | 'empty'
  | 'invalid'
  | 'unsupportedHost'
  | 'missingId'

export type ParseYoutubeResult =
  | { ok: true; videoId: string; hintSeconds?: number }
  | { ok: false; error: YoutubeUrlErrorCode }

function parseTimeParam(value: string | null): number | undefined {
  if (!value) return undefined
  if (/^\d+(\.\d+)?$/.test(value)) {
    const n = Number(value)
    return Number.isFinite(n) && n >= 0 ? n : undefined
  }
  // YouTube t=1h2m3s style
  const match = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+(?:\.\d+)?)s)?$/)
  if (!match) return undefined
  const hours = Number(match[1] ?? 0)
  const minutes = Number(match[2] ?? 0)
  const seconds = Number(match[3] ?? 0)
  const total = hours * 3600 + minutes * 60 + seconds
  return Number.isFinite(total) && total >= 0 ? total : undefined
}

/**
 * Parse youtube.com/watch, youtu.be, and embed URLs into a video ID.
 * Rejects unsupported providers. Never executes iframe HTML.
 * Errors are codes for UI i18n — not user-facing English strings.
 */
export function parseYoutubeUrl(input: string): ParseYoutubeResult {
  const trimmed = input.trim()
  if (!trimmed) {
    return { ok: false, error: 'empty' }
  }

  let url: URL
  try {
    url = new URL(trimmed)
  } catch {
    // Bare video ID
    if (YOUTUBE_ID_RE.test(trimmed)) {
      return { ok: true, videoId: trimmed }
    }
    return { ok: false, error: 'invalid' }
  }

  const host = url.hostname.replace(/^www\./, '').toLowerCase()
  let videoId: string | null = null

  if (host === 'youtu.be') {
    videoId = url.pathname.split('/').filter(Boolean)[0] ?? null
  } else if (
    host === 'youtube.com' ||
    host === 'm.youtube.com' ||
    host === 'music.youtube.com' ||
    host === 'youtube-nocookie.com'
  ) {
    if (url.pathname === '/watch') {
      videoId = url.searchParams.get('v')
    } else if (url.pathname.startsWith('/embed/')) {
      videoId = url.pathname.split('/')[2] ?? null
    } else if (url.pathname.startsWith('/shorts/')) {
      videoId = url.pathname.split('/')[2] ?? null
    } else if (url.pathname.startsWith('/live/')) {
      videoId = url.pathname.split('/')[2] ?? null
    }
  } else {
    return { ok: false, error: 'unsupportedHost' }
  }

  if (!videoId || !YOUTUBE_ID_RE.test(videoId)) {
    return { ok: false, error: 'missingId' }
  }

  const hint =
    parseTimeParam(url.searchParams.get('t')) ??
    parseTimeParam(url.searchParams.get('start'))

  return hint !== undefined
    ? { ok: true, videoId, hintSeconds: hint }
    : { ok: true, videoId }
}

export function youtubeUrlErrorKey(
  code: YoutubeUrlErrorCode,
): `youtube.error.${YoutubeUrlErrorCode}` {
  return `youtube.error.${code}`
}
